import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { cloneDeep } from "lodash";
import { Observable, of } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { UnitTypeLabelEnum } from "src/app/shared/Models/enums/UnitTypeLabelEnum";
import { MduService } from "src/app/shared/services/mdu.service";
import { AddressTypeEnum } from "../../../../shared/Models/enums/AddressTypeEnum";
import { BuildingTypeEnum } from "../../../../shared/Models/enums/BuildingTypeEnum";
import {
  SaveOutsideSurveyUnitInfoAddressTypeDto,
  SaveOutsideSurveyUnitInfoDto,
} from "../../../../shared/Models/write/SaveOutsideSurveyUnitInfoDto";
import { ToastService } from "../../../../shared/toast.service";
import {
  addressTypeKey,
  buildingTypeKey,
  OutsideUnitBaseComponent,
  studentHomeCountKey,
} from "../../shared/components/outside-unit-base.component";
import { buildingTypeValidationRules } from "../../shared/services/building-type-validation.service";
import { DeviceCheckService } from "../../shared/services/device-check.service";
import { SavingStateService } from "../../shared/services/saving-state.service";
import {
  OutsideUnitsEnum,
  OUTSIDE_SURVEY_OUTSIDE_INFO,
  OUTSIDE_SURVEY_OUTSIDE_UNITS,
  OUTSIDE_SURVEY_PHOTOS,
} from "../config/outside-survey.magic-strings";
import { FormValidationService } from "../../shared/services/formValidation.service";
import { buildingTypeMessages } from "../../shared/services/building-type-validation.messages";
import { BuildingService } from "src/app/core/building-list/services/building.service";
import { MainDistributionEnum } from "src/app/shared/Models/enums/MainDistributionEnum";
const addressKey = "address";

@Component({
  selector: "app-outside-survey-unit",
  templateUrl: "./outside-survey-unit.component.html",
  styleUrls: ["./outside-survey-unit.component.scss"],
})
export class OutsideSurveyUnitComponent
  extends OutsideUnitBaseComponent
  implements OnInit, OnDestroy
{
  tabId = OUTSIDE_SURVEY_OUTSIDE_UNITS;
  private originalUnitQuestions;
  private originalFloorQuestions;

  @Input() isAuthCanWrite: boolean;
  totalUnits: number = 0;
  landscape: boolean;
  BuildingTypeEnum = BuildingTypeEnum;
  outsideUnitsEnum = OutsideUnitsEnum;

  constructor(
    private toastService: ToastService,
    private _savingStateService: SavingStateService,
    private _mduService: MduService,
    private _deviceCheckService: DeviceCheckService,
    private formValidationService: FormValidationService,
    private buildingService: BuildingService
  ) {
    super();
  }

  ngOnInit() {
    this.formValidationService.addFormError([OUTSIDE_SURVEY_OUTSIDE_UNITS], {
      [OutsideUnitsEnum.NUMBER_OF_ROOMS_FIELD]: false,
      [OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD]: false,
      [OutsideUnitsEnum.BUILDING_CHARACTERISTIC_FIELD]: false,
      [OutsideUnitsEnum.UNITS_FIELD]: false,
      [OutsideUnitsEnum.FLOORS_FIELD]: false,
    });
    this.survey.building.addresses.forEach((a) =>
      this.formValidationService.addFormError(
        [OUTSIDE_SURVEY_OUTSIDE_UNITS, a.id],
        {
          [OutsideUnitsEnum.NUMBER_OF_ROOMS_FIELD]: false,
          [OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD]: false,
        }
      )
    );
    this.landscape = !this._deviceCheckService.widthCheck(1000);
    this.unitQuestions.map((u) => {
      if (this.survey.hasOwnProperty(u.key)) {
        u.quantity = this.survey[u.key] === null ? 0 : this.survey[u.key];
        u.originalValue =
          this.survey.building[u.key] === null
            ? 0
            : this.survey.building[u.key];
      }
      return u;
    });
    this.init([this.survey.building]);
    this.setAdressTypePossibilities(this.survey.building);
    this.originalUnitQuestions = cloneDeep(this.unitQuestions);
    this.floorQuestions.map((u) => {
      if (this.survey.hasOwnProperty(u.key))
        u.quantity = this.survey[u.key] === null ? 0 : this.survey[u.key];

      return u;
    });
    this.originalFloorQuestions = cloneDeep(this.floorQuestions);
    this.calculateTotalUnits();
    this.updateAddressTypes();

    Object.keys(this.unitForms).forEach((s) => {
      this.unitForms[s].valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          var formAddresses: {
            id: string;
            addressType: AddressTypeEnum;
          }[] = new Array<{
            id: string;
            addressType: AddressTypeEnum;
          }>();

          this.survey.building.addresses.map((addr, i) => {
            let term = `addressType${i}`;

            var formAddress: {
              id: string;
              addressType: AddressTypeEnum;
            } = { id: addr.id, addressType: res[term] };

            formAddresses[i] = formAddress;
          });
          this.updateAddressTypes();
        });
    });
  }

  @HostListener("window:resize")
  onResize() {
    this.landscape = !this._deviceCheckService.widthCheck(1000);
  }

  buildingTypesDuringOutsideSurvey = Object.keys(BuildingTypeEnum)
    .filter((key) => !isNaN(+key))
    .map((key) => ({
      value: +key,
      label: BuildingTypeEnum[key],
    }));

  addressTypesForBuilding: any[] = [];

  updateAddressTypes() {
    this.addressTypesForBuilding = this.addressTypeTexts[
      this.survey.building.id
    ].map((a) => ({
      label: AddressTypeEnum[a],
      value: a,
      disabled: false,
    }));
  }

  onQuantityChange(newQuantity: number, questionKey: string) {
    if (!this.isAuthCanWrite || newQuantity > 1000 || newQuantity < 0) return;

    const unitQuestion = this.unitQuestions.find((u) => u.key === questionKey);
    if (unitQuestion) unitQuestion.quantity = newQuantity;

    const floorQuestion = this.floorQuestions.find(
      (u) => u.key === questionKey
    );
    if (floorQuestion) floorQuestion.quantity = newQuantity;

    this.calculateTotalUnits();
    this.setAdressTypePossibilities(
      this.survey.building,
      this.unitForms[this.survey.building.id].get(buildingTypeKey).value
    );
    this.updateAddressTypes();
  }

  calculateTotalUnits() {
    this.totalUnits = this.unitQuestions.reduce((a, i) => a + i.quantity, 0);
    return this.totalUnits;
  }

  get validateMDuFlag(): boolean {
    this.survey.isMDU = this._mduService.isMdu(
      this.unitQuestions.filter((x) => x.label == UnitTypeLabelEnum.Lu)[0]
        .quantity,
      this.unitQuestions.filter((x) => x.label == UnitTypeLabelEnum.Bu)[0]
        .quantity,
      this.unitQuestions.filter((x) => x.label == UnitTypeLabelEnum.Su)[0]
        .quantity
    );
    return this.survey.isMDU;
  }

  save(checkValidity?: boolean): Observable<any> {
    if (!this.isAuthCanWrite) return of(true);
    var dto = this.createDto();
    if (checkValidity && !this.isTabValid(dto)) return of(false);
    if (this.isChanged())
      return this._savingStateService.addOutsideSurveyUnit(dto).pipe(
        tap(() => {
          this.originalUnitQuestions = cloneDeep(this.unitQuestions);
          this.originalFloorQuestions = cloneDeep(this.floorQuestions);
          this.survey.building.buildingType =
            this.unitForms[this.survey.building.id].get(buildingTypeKey).value;

          this.survey.building.studentHomeCount = this.unitForms[
            this.survey.building.id
          ].get("building" + studentHomeCountKey)?.value
            ? this.unitForms[this.survey.building.id].get(
                "building" + studentHomeCountKey
              ).value
            : null;

          this.survey.building.addresses.forEach((address, index) => {
            address.addressType = this.unitForms[this.survey.building.id]?.get(
              addressTypeKey + index
            ).value;
            address.studentHomeCount = this.unitForms[
              this.survey.building.id
            ]?.get(`address${address.id}${studentHomeCountKey}`)?.value;
          });
        })
      );
    else return of(true);
  }

  createDto() {
    const obj = {
      surveyId: this.survey.id,
      buildingId: this.survey.building.id,
      buildingType:
        this.unitForms[this.survey.building.id].get(buildingTypeKey).value ==
        "-1"
          ? null
          : this.unitForms[this.survey.building.id].get(buildingTypeKey).value,
      studentHomeCount: this.unitForms[this.survey.building.id]?.get(
        "building" + studentHomeCountKey
      )
        ? this.unitForms[this.survey.building.id].get(
            "building" + studentHomeCountKey
          ).value
        : null,
      changeAddressTypes: this.survey.building.addresses.map(
        (address, index) => {
          var selectedValue = this.unitForms[this.survey.building.id].get(
            addressTypeKey + index
          ).value;
          var selectedStudentHomeCount = this.unitForms[
            this.survey.building.id
          ].get("address" + address.id + studentHomeCountKey)
            ? this.unitForms[this.survey.building.id].get(
                "address" + address.id + studentHomeCountKey
              )?.value
            : null;
          if (selectedValue == "-1") selectedValue = null;
          return new SaveOutsideSurveyUnitInfoAddressTypeDto(
            address.id,
            selectedValue,
            selectedStudentHomeCount
          );
        }
      ),
    };

    this.unitQuestions.forEach((u) => (obj[u.key] = u.quantity ?? 0));
    this.floorQuestions.forEach((u) => (obj[u.key] = u.quantity ?? 0));

    return new SaveOutsideSurveyUnitInfoDto().deserialize(obj);
  }

  isChanged() {
    const unitQuestionsChanged = this.originalUnitQuestions.some(
      (u, index) => u.quantity !== this.unitQuestions[index].quantity
    );
    const floorQuestionsChanged = this.originalFloorQuestions.some(
      (u, index) => u.quantity !== this.floorQuestions[index].quantity
    );

    var addressChanged = this.survey.building.addresses.some(
      (address, index) => {
        return (
          address.addressType !==
            this.unitForms[this.survey.building.id].get(addressTypeKey + index)
              .value ||
          address.studentHomeCount !=
            this.unitForms[this.survey.building.id].get(
              "address" + address.id + studentHomeCountKey
            )?.value
        );
      }
    );
    var buildingChanged =
      this.survey.building.studentHomeCount !=
      this.unitForms[this.survey.building.id].get(
        "building" + studentHomeCountKey
      )?.value;

    return (
      unitQuestionsChanged ||
      floorQuestionsChanged ||
      addressChanged ||
      buildingChanged ||
      this.survey.building.buildingType !==
        this.unitForms[this.survey.building.id].get(buildingTypeKey)?.value
    );
  }

  isTabValid(dto: SaveOutsideSurveyUnitInfoDto): boolean {
    this.formValidationService.resetErrorsForPath(OUTSIDE_SURVEY_OUTSIDE_UNITS);
    let isValid = true;
    let buildingTypeKeyValue =
      this.unitForms[this.survey.building.id].get(buildingTypeKey).value;

    if (
      dto.numberOfFloors > 0 &&
      dto.buildingType == this.buildingTypeEnum.NotInUse
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.building.zerofloor:Building requires 0 floors.`
      );
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_UNITS,
        OutsideUnitsEnum.FLOORS_FIELD
      );
    }

    if (
      (dto.numberOfSu > 0 ||
        dto.numberOfLu > 0 ||
        dto.numberOfBu > 0 ||
        dto.numberOfLBu > 0 ||
        dto.numberOfMBu > 0) &&
      dto.buildingType == this.buildingTypeEnum.NotInUse
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.building.zerounits:Building requires 0 units.`
      );
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_UNITS,
        OutsideUnitsEnum.UNITS_FIELD
      );
    }

    if (isValid == false) return false;

    if (this.survey.building.buildingType == BuildingTypeEnum.NotInUse)
      return true;

    if (dto.numberOfFloors <= 0) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.building.atleastonefloor:Building requires at least 1 floor.`
      );
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_UNITS,
        OutsideUnitsEnum.FLOORS_FIELD
      );
    }

    if (buildingTypeKeyValue == "-1" || buildingTypeKeyValue == null) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.building.selecttype:Building type is required!`
      );
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_UNITS,
        OutsideUnitsEnum.BUILDING_CHARACTERISTIC_FIELD
      );
    }

    if (
      dto.buildingType == this.buildingTypeEnum.InUse &&
      dto.changeAddressTypes.every(
        (a) => a.addressType == AddressTypeEnum.NotInUse
      )
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.building.atLeastOneNotNotInUse:At least one address should be different from Not In Use for a building of type In Use!`
      );
      isValid = false;
      dto.changeAddressTypes
        .filter((a) => a.addressType == this.addressTypeEnum.NotInUse)
        .forEach((a) => {
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            a.addressId,
            OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
          );
        });
    }

    if (
      buildingTypeKeyValue == BuildingTypeEnum.EmptyParcel &&
      this.survey.mainDistribution == MainDistributionEnum.Facade
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.building.emptyParcelTypeWithFacade:The building characteristic cannot be 'EmptyParcel' when the Main Distribution type is 'Facade'.`
      );
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_UNITS,
        OutsideUnitsEnum.BUILDING_CHARACTERISTIC_FIELD
      );
    }

    if (this.survey.building.buildingGroupId) {
      if (
        buildingTypeKeyValue == BuildingTypeEnum.EmptyParcel ||
        buildingTypeKeyValue == BuildingTypeEnum.NotInUse
      ) {
        this.toastService.showWarnToast(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.address.invalidBuildingTypeForBuildingGroup:NotInUse and EmptyParcel are invalid building types for a building in a group!`
        );
        isValid = false;
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_UNITS,
          OutsideUnitsEnum.BUILDING_CHARACTERISTIC_FIELD
        );
      }

      if (this.survey.buildingsFromBuildingGroup) {
        const allBuildingsNoAccess =
          this.survey.buildingsFromBuildingGroup.every(
            (building) => building.buildingType == BuildingTypeEnum.NoAccess
          );

        if (
          buildingTypeKeyValue == BuildingTypeEnum.NoAccess &&
          !allBuildingsNoAccess
        ) {
          this.toastService.showWarnToast(
            $localize`:@@general.warning:Warning`,
            $localize`:@@validation.survey.address.invalidBuildingTypeNoAccessForBuildingGroup:NoAccess can only be used for a building in a group where every building is of the same type!`
          );
          isValid = false;
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.BUILDING_CHARACTERISTIC_FIELD
          );
        }
      }
    }

    dto.changeAddressTypes
      .filter((a) => a.addressType == undefined)
      .forEach((a) => {
        this.toastService.showWarnToast(
          $localize`:@@general.warning: Warning`,
          $localize`:@@validation.survey.addressCharacteristicRequired: Address characteristic is required.`
        );
        isValid = false;
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_UNITS,
          a.addressId,
          OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
        );
      });

    dto.changeAddressTypes
      .filter((a) => a.addressType == AddressTypeEnum.StudentHome)
      .forEach((a) => {
        if ((dto.numberOfBu || 0) < 1) {
          this.toastService.showWarnToast(
            $localize`:@@general.warning: Warning`,
            $localize`:@@validation.survey.studentHomeBUGreaterThanZero: A student home needs at least 1 Bussines Unit.`
          );
          isValid = false;
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.UNITS_FIELD
          );
        }

        if ((a.studentHomeCount || 0) < 1) {
          this.toastService.showWarnToast(
            $localize`:@@general.warning: Warning`,
            $localize`:@@validation.survey.studentHomeGreaterThanZero: A student home needs at least 1 Student Home.`
          );
          isValid = false;
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            a.addressId,
            OutsideUnitsEnum.NUMBER_OF_ROOMS_FIELD
          );
        }
      });

    if (
      dto.changeAddressTypes.some(
        (a) => a.addressType == this.addressTypeEnum.EmptyParcel
      ) &&
      dto.buildingType != this.buildingTypeEnum.EmptyParcel
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning: Warning`,
        $localize`:@@validation.survey.emptyParcelAddressButNotBuilding: An address can only be of type Empty Parcel if the Building is of that same type.`
      );
      isValid = false;
      dto.changeAddressTypes
        .filter((a) => a.addressType == this.addressTypeEnum.EmptyParcel)
        .forEach((a) => {
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            a.addressId,
            OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
          );
        });
    }

    if (
      dto.changeAddressTypes.some(
        (a) => a.addressType == this.addressTypeEnum.NoAccess
      )
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning: Warning`,
        $localize`:@@validation.survey.NoAccessAddressButNotBuilding: An address cannot be of type No Access.`
      );
      isValid = false;
      dto.changeAddressTypes
        .filter((a) => a.addressType == this.addressTypeEnum.NoAccess)
        .forEach((a) => {
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            a.addressId,
            OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
          );
        });
    }
    let buildingTypeValidation = buildingTypeValidationRules[
      buildingTypeKeyValue
    ](dto, this.totalUnits);
    if (typeof buildingTypeValidation == "object") {
      this.toastService.showWarnToast(
        buildingTypeValidation.title,
        buildingTypeValidation.message
      );
      isValid = false;
      switch (buildingTypeValidation) {
        case buildingTypeMessages.emptyParcelAtleastOneUnit:
        case buildingTypeMessages.inUseAtleastOneUnit:
        case buildingTypeMessages.inUseAtleastOneMultipleUnits:
        case buildingTypeMessages.noAccesAtleastTwoUnits:
        case buildingTypeMessages.notInUse0Units:
        case buildingTypeMessages.studentHomeAtleastOneBU:
        case buildingTypeMessages.studentHomeAtleastOneUnit:
        case buildingTypeMessages.inUseWithoutBuildingUnit:
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.UNITS_FIELD
          );
          break;
        case buildingTypeMessages.studentHomeAtleastOneStudentHome:
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.NUMBER_OF_ROOMS_FIELD
          );
          break;
        case buildingTypeMessages.inUseMultipleBuildingType:
        case buildingTypeMessages.buildingCharacteristicEmpty:
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.BUILDING_CHARACTERISTIC_FIELD
          );
          break;
        case buildingTypeMessages.studentHomeCountNotEqual:
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.NUMBER_OF_ROOMS_FIELD
          );
          dto.changeAddressTypes.forEach((a) =>
            this.formValidationService.addError(
              OUTSIDE_SURVEY_OUTSIDE_UNITS,
              a.addressId,
              OutsideUnitsEnum.NUMBER_OF_ROOMS_FIELD
            )
          );
          break;
        case buildingTypeMessages.inUseZeroUnitsAddressAtLeastOne:
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
          );
          dto.changeAddressTypes.forEach((a) =>
            this.formValidationService.addError(
              OUTSIDE_SURVEY_OUTSIDE_UNITS,
              a.addressId,
              OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
            )
          );
          break;
        default:
          break;
      }
    }
    if (
      this.survey.building.addresses.some((address, index) => {
        return (
          this.unitForms[this.survey.building.id].get(addressTypeKey + index)
            .value == -1
        );
      })
    ) {
      this.toastService.showWarnToast(
        $localize`:@@general.warning:Warning`,
        $localize`:@@validation.survey.address.selecttype:Address type is required!`
      );
      isValid = false;
      this.survey.building.addresses
        .filter(
          (address, index) =>
            this.unitForms[this.survey.building.id].get(addressTypeKey + index)
              .value == -1
        )
        .forEach((a) => {
          this.formValidationService.addError(
            OUTSIDE_SURVEY_OUTSIDE_UNITS,
            a.id,
            OutsideUnitsEnum.ADDRESS_CHARACTERISTIC_FIELD
          );
        });
    }
    return isValid;
  }

  onChange(event) {
    if (event.value == BuildingTypeEnum.NotInUse) {
      this.unitQuestions.forEach((unitQuestion) => {
        this.onQuantityChange(0, unitQuestion.key);
      });
      this.floorQuestions.forEach((unitQuestion) => {
        this.onQuantityChange(0, unitQuestion.key);
      });
      this.formValidationService.resetErrorsForPath(
        OUTSIDE_SURVEY_OUTSIDE_UNITS
      );
      this.formValidationService.resetErrorsForPath(
        OUTSIDE_SURVEY_OUTSIDE_INFO
      );
      this.formValidationService.resetErrorsForPath(OUTSIDE_SURVEY_PHOTOS);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  getErrors(...path: string[]) {
    return this.formValidationService.getErrors(
      ...[OUTSIDE_SURVEY_OUTSIDE_UNITS, ...path].filter((x) => !!x)
    );
  }
}
